import { useSelector } from "react-redux"
import { Box, Typography, Grid, Button, InputBase, Chip, Stack, Tabs, Tab } from "@mui/material";
import { useEffect, useMemo, useState, useRef } from "react";
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { Search, Close } from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import getBrand from "../brands/getBrand";
import AddToCartButton from "./AddToCartButton";
import { useFetchCart } from "./globalFunctions";
import { getAppConfig } from "../utils/getAppConfig";
import { Snackbar, Alert } from "@mui/material";

const Home = ({ hideFooter = false }) => {
  const brand = getBrand(); 
  const hub = localStorage.getItem('selected_hub');
  const productsFromStore = useSelector(state => state.products.products || []);
  const userCart = useSelector(state => state.user.userCart || []);
  const userId = useSelector(state => state.user.id);
  const selectedHub = useSelector(state => state.user.selected_hub);
  const navigate = useNavigate();
  const user_role = localStorage.getItem('role');
  const [productsType, setProductsType] = useState('vegitables');
  const [tabValue, setTabValue] = useState('walkin');
  const [subcategorySidebarOpen, setSubcategorySidebarOpen] = useState(false);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);
  const [selectedCaps, setSelectedCaps] = useState({});
  const fetchCart = useFetchCart();
  const hasCartBeenFetched = useRef(false);
  const [Filters, setFilters] = useState({
    search: "",
    categoryId: "",
    subCategoryName: "",
  });
  const [footerText, setFooterText] = useState("");
  const [expandedTitles, setExpandedTitles] = useState({});
  const [overflowMap, setOverflowMap] = useState({});
  const [stockAlertProduct, setStockAlertProduct] = useState(null);
  useEffect(() => {
    setProductsType('vegitables');
    // Trigger products fetch by accessing brand.products
    // This will trigger the getter and fetch products automatically
    if (brand && brand.products) {
      const products = brand.products;
    } else {
      console.warn("[Home] brand or brand.products is not available when product-load effect ran");
    }
  }, [brand, selectedHub]);

  // Debug: Log products changes for merchant role
  useEffect(() => {
    if (user_role === 'merchant') {
      console.log('[Home - Merchant] Products updated:', {
        timestamp: new Date().toISOString(),
        totalProducts: productsFromStore.length,
        selectedHub,
        products: productsFromStore.map(p => ({ 
          id: p.id, 
          name: p.item_name, 
          inventory: p.inventory, 
          inventory_id: p.inventory_id 
        }))
      });
    }
  }, [productsFromStore, user_role, selectedHub]);

  // Listen for header hamburger toggles
  useEffect(() => {
    const handler = () => setCategoryMenuOpen((prev) => !prev);
    window.addEventListener("toggle-category-menu", handler);
    return () => window.removeEventListener("toggle-category-menu", handler);
  }, []);

  // Get brand images/products with a fallback to Redux products
  

  // Unique categories from products (item_category)
  const categories = useMemo(() => {
    const items = Array.isArray(productsFromStore) ? productsFromStore : [];
    const set = new Set();
    items.forEach(p => {
      if (p.item_category) set.add(p.item_category);
    });
    return Array.from(set);
  }, [productsFromStore]);

  // Filter images based on search and category/subcategory
  const filtered = useMemo(() => {
    let list = productsFromStore || [];
    
    // Filter products for merchant role based on subdomain/hub
    if (user_role === 'merchant') {
      // Get merchant's hub from localStorage or Redux
      let merchantHubId = null;
      try {
        const storedHub = localStorage.getItem('hub');
        if (storedHub) {
          const parsed = JSON.parse(storedHub);
          merchantHubId = Array.isArray(parsed) && parsed.length > 0 ? parsed[0] : parsed;
        }
        if (!merchantHubId && selectedHub) {
          merchantHubId = Array.isArray(selectedHub) ? selectedHub[0] : selectedHub;
        }
      } catch (e) {
        console.error("[Home] Error parsing merchant hub:", e);
      }
      
      if (merchantHubId) {
        // Filter products that belong to merchant's inventory
        const beforeFilter = list.length;
        list = list.filter(product => {
          // Check if product is available in merchant's hub
          const hasInventoryMatch = product.inventory && product.inventory.some(inv => inv.id === merchantHubId);
          const hasInventoryIdMatch = product.inventory_id === merchantHubId;
          const hasCapacityMatch = product.capacitydetails && Object.keys(product.capacitydetails).some(hubId => parseInt(hubId) === merchantHubId);
          
          return hasInventoryMatch || hasInventoryIdMatch || hasCapacityMatch;
        });
        
        // Console log for debugging
        console.log('[Home - Merchant] Filtering products:', {
          merchantHubId,
          totalProductsBeforeFilter: beforeFilter,
          totalProductsAfterFilter: list.length,
          products: list.map(p => ({ id: p.id, name: p.item_name, inventory: p.inventory, inventory_id: p.inventory_id }))
        });
      } else {
        console.warn('[Home - Merchant] No merchant hub ID found, showing all products');
      }
    }
    
    if (Filters.search.trim()) {
      const q = Filters.search.toLowerCase();

      list = list.filter(item =>
        (item.item_name || "").toLowerCase().includes(q) ||
        (item.sku_code || "").toLowerCase().includes(q) ||
        (item.product_barcode || "").toLowerCase().includes(q)
      );
    }
    if (Filters.categoryId) {
      list = list.filter(item => (item.item_category || "").toLowerCase() === Filters.categoryId.toLowerCase());
    }
    if (Filters.subCategoryName) {
      list = list.filter(item => {
        const itemSubCategory = (item.subCategoryName || item.sub_category_name || "").toLowerCase();
        return itemSubCategory === Filters.subCategoryName.toLowerCase();
      });
    }
    return list;
  }, [Filters, productsFromStore, user_role, selectedHub]);

  const getCapsForHub = (product) => {
    const hubId = hub ? String(hub) : null;
    const source = product?.capacitydetails || product?.capacities || [];
    if (Array.isArray(source)) return source;
    if (hubId && source[hubId]?.capacities) return source[hubId].capacities;
    if (hubId && source[parseInt(hubId, 10)]?.capacities) return source[parseInt(hubId, 10)].capacities;
    return [];
  };

  const getPrice = (product) => {
    const capacityList = getCapsForHub(product);
    const selected = selectedCaps[product.id];
    const capacity = Array.isArray(capacityList)
      ? (selected ? capacityList.find(c => c.item_capacity === selected) : capacityList[0])
      : null;
    const price = capacity?.item_selling_price || capacity?.item_price || product.item_selling_price || product.item_price || product.price;
    return price ? Number(price) : null;
  };

  // const getTotalInventoryUnits = (product) => {
  //   const caps = getCapsForHub(product);

  //   const totalUnits = caps.reduce((total, cap) => {
  //     return total + (parseInt(cap.available_units) || 0);
  //   }, 0);

  //   console.log("TOTAL STOCK:", totalUnits);

  //   return totalUnits;
  // };

  // UNIVERSAL UNIT CONVERTER
  const convertToBaseUnit = (value, unit) => {
    const numeric = parseFloat(value) || 0;
    if (!unit) return numeric;

    const u = unit.toLowerCase();

    // WEIGHT
    if (u.includes("kg")) return numeric * 1000; // convert to grams
    if (u.includes("g")) return numeric; // already grams

    // LIQUID
    if (u.includes("litre") || u.includes("ltr") || u === "l")
      return numeric * 1000; // convert to ml
    if (u.includes("ml")) return numeric;

    // COUNTABLE
    if (u.includes("packet") || u.includes("box") || u.includes("piece"))
      return numeric;

    return numeric;
  };

  const getTotalInventoryUnits = (product) => {
    const rawStock =
      product.available_units ??
      product.total_units ??
      product.inventory_details?.available_units ??
      product.inventryDetails?.available_units ??
      0;
    const stock = parseFloat(rawStock) || 0;

    const unitFromProduct =
      product.unit ||
      product.inventory_details?.unit ||
      product.inventryDetails?.unit ||
      "kg";

    return convertToBaseUnit(stock, unitFromProduct);
  };

  const getCapacityWeight = (capacityValue, unit) => {
    return convertToBaseUnit(capacityValue, unit);
  };


  const getTotalCartWeight = (productId) => {
    if (!Array.isArray(userCart)) return 0;

    const cartItem = userCart.find(item => item.product_id === productId);
    if (!cartItem) return 0;

    let total = 0;

    cartItem.capacity?.forEach(cap => {
      const weight = getCapacityWeight(cap.capacityValue, cap.unit);
      const qty = parseInt(cap.item_quantity) || 0;
      total += weight * qty;
    });

    return total;
  };
  const getCartQuantity = (productId, selectedCapacityValue) => {
    if (!Array.isArray(userCart)) return 0;

    const cartItem = userCart.find(item => item.product_id === productId);
    if (!cartItem) return 0;

    const matched = cartItem.capacity?.find(
      cap => cap.item_capacity === selectedCapacityValue
    );

    return matched?.item_quantity || 0;
  };



  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleUpdateCart = async () => {
    if (userId) {
      const hubId = Array.isArray(selectedHub) ? selectedHub[0] : selectedHub;
      await fetchCart(userId, hubId, productsType || 'online');
    }
  };

  // Fetch cart once on mount to populate quantities (only once when userId and selectedHub are available)
  useEffect(() => {
    if (userId && selectedHub && !hasCartBeenFetched.current) {
      hasCartBeenFetched.current = true;
      const hubId = Array.isArray(selectedHub) ? selectedHub[0] : selectedHub;
      fetchCart(userId, hubId, productsType || 'online');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, selectedHub]);

  // Update footer text when hub changes and on initial load
  useEffect(() => {
    const updateFooter = async () => {
      // Get hub ID from various sources
      let hubId = null;
      if (selectedHub) {
        hubId = Array.isArray(selectedHub) ? selectedHub[0] : selectedHub;
      } else {
        // Fallback to localStorage if selectedHub not in Redux
        const hubFromStorage = localStorage.getItem("selected_hub");
        if (hubFromStorage) {
          try {
            const parsed = JSON.parse(hubFromStorage);
            hubId = Array.isArray(parsed) ? parsed[0] : parsed;
          } catch (e) {
            hubId = parseInt(hubFromStorage, 10);
          }
        }
      }

      if (selectedHub) {
        // Fetch AppConfig first to ensure it's cached
        await getAppConfig();
        // Then get footer text
        const text = brand.footer;
        setFooterText(text || "");
      } else {
        // Still try to get footer even without hub ID
        const text = brand.footer;
        setFooterText(text || "");
      }
    };
    
    updateFooter();
  }, [selectedHub]);

  // Also check on initial mount
  useEffect(() => {
    const text = brand.footer;
    if (text) {
      setFooterText(text);
    }
  }, []);

  // 🔥 Calculate total inventory value for all products
  const getTotalInventoryValue = (product) => {
    const stock =
      parseFloat(product.available_units) ||
      parseFloat(product.total_units) ||
      0;

    const caps = getCapsForHub(product);
    if (!caps.length) return 0;

    const basePrice =
      parseFloat(caps[0].item_selling_price || caps[0].item_price) || 0;

    return stock * basePrice;
  };

  const productList = filtered.length > 0 ? filtered : productsFromStore;

  const overallInventoryValue = productList.reduce((total, product) => {
    return total + getTotalInventoryValue(product);
  }, 0);

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        background: brand?.backgroundColor || "#F9FAFB",
        padding: { xs: "12px", sm: "16px", md: "24px" },
        display: "flex",
        flexDirection: "column",
        position: "relative"
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: { xs: "flex-start", sm: "center" }, paddingBottom: {  md: "16px" }, gap: { xs: "8px", md: "16px" }, flexWrap: "wrap", flexDirection: { xs: "column", sm: "row" } }}>
        {user_role !== 'cashier' && user_role !== 'user' && (
          <Box sx={{ display: "flex", alignItems: "center", gap: { xs: "8px", md: "16px" }, width: { xs: "100%", sm: "auto" }, flexShrink: 0 }}>
            <Button 
              startIcon={<ArrowBackIosIcon />} 
              onClick={() => navigate('/home/dashboard')}
              sx={{ textTransform: "none", minWidth: "auto", fontSize: { xs: "12px", md: "14px" } }}
            >
              Back
            </Button>
            <Typography sx={{ fontWeight: "600", fontSize: { xs: "16px", md: "20px" }, color: brand?.theme?.textPrimary || "#111827" }}>
              {brand.headerTitle || "Hub Products"}
            </Typography>
          </Box>
        )}
        <Box sx={{ 
          width: { xs: "100%", sm: "50%" }, 
          display: "flex", 
          justifyContent: "center", 
          flexShrink: 0
        }}>
          <InputBase 
            value={Filters.search} 
            startAdornment={<Search sx={{ color: "#9AA0A6", marginRight: "8px", fontSize: { xs: "18px", md: "24px" } }} />} 
            placeholder='Search products...' 
            onChange={(e) => { setFilters({ ...Filters, search: e.target.value }) }} 
            sx={{ 
              width: "100%",
              p: { xs: "8px 12px", md: "12px 16px" }, 
              border: "1px solid #dfe1e5", 
              borderRadius: "24px", 
              height: { xs: "36px", md: "44px" },
              fontSize: { xs: "14px", md: "16px" },
              background: "#fff",
              boxSizing: "border-box",
              minWidth: 0
            }} 
          />
        </Box>
      </Box>

      {/* Overlay when category drawer is open on mobile */}
      {categoryMenuOpen && (
        <Box
          onClick={() => setCategoryMenuOpen(false)}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 1299,
            display: { xs: "block", md: "none" }
          }}
        />
      )}

      {/* Mobile Category Drawer - Side drawer */}
      <Box sx={{
        position: "fixed",
        left: categoryMenuOpen ? 0 : "-100%",
        top: 0,
        bottom: 0,
        width: "280px",
        background: "#fff",
        borderRight: "1px solid #E2E8F0",
        zIndex: 1300,
        boxShadow: "2px 0 8px rgba(0,0,0,0.1)",
        display: { xs: "flex", md: "none" },
        flexDirection: "column",
        padding: "16px",
        overflowY: "auto",
        transition: "left 0.3s ease-in-out"
      }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography sx={{ fontSize: "16px", fontWeight: 600 }}>Categories</Typography>
          <Button
            onClick={() => setCategoryMenuOpen(false)}
            sx={{
              minWidth: "auto",
              padding: "4px",
              color: "#6B7280",
              "&:hover": {
                background: "#F3F4F6"
              }
            }}
          >
            <Close sx={{ fontSize: "20px" }} />
          </Button>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
          <Button
            onClick={() => {
              setFilters({ ...Filters, categoryId: "" });
              setCategoryMenuOpen(false);
            }}
            sx={{
              textTransform: "none",
              justifyContent: "flex-start",
              fontSize: "13px",
              color: !Filters.categoryId ? (brand?.themeColor || "#ED1E79") : (brand?.theme?.textSecondary || "#6B7280"),
              fontWeight: !Filters.categoryId ? 600 : 400,
              px: "12px",
              py: "8px",
              borderRadius: "8px",
              "&:hover": {
                background: brand?.theme?.hoverBackground || "#F3F4F6"
              }
            }}
          >
            All categories
          </Button>
          {categories.map(cat => (
            <Button
              key={cat}
              onClick={() => {
                setFilters({ ...Filters, categoryId: cat });
                setCategoryMenuOpen(false);
              }}
              sx={{
                textTransform: "none",
                justifyContent: "flex-start",
                fontSize: "13px",
                color: Filters.categoryId === cat ? (brand?.themeColor || "#ED1E79") : (brand?.theme?.textSecondary || "#6B7280"),
                fontWeight: Filters.categoryId === cat ? 600 : 400,
                px: "12px",
                py: "8px",
                borderRadius: "8px",
                "&:hover": {
                  background: brand?.theme?.hoverBackground || "#F3F4F6"
                }
              }}
            >
              {cat}
            </Button>
          ))}
        </Box>
      </Box>
      
      {/* Overlay when sidebar is open on mobile */}
      {productsType === 'vegitables' && subcategorySidebarOpen && (
        <Box
          onClick={() => setSubcategorySidebarOpen(false)}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 1199,
            display: { xs: "block", md: "none" }
          }}
        />
      )}
      
      {/* Mobile Sidebar - Fixed overlay (only on mobile) */}
      {productsType === 'vegitables' && (
        <Box sx={{
          position: "fixed",
          left: subcategorySidebarOpen ? 0 : "-100%",
          top: 0,
          bottom: 0,
          width: "280px",
          background: "#fff",
          borderRight: "1px solid #E2E8F0",
          zIndex: 1200,
          boxShadow: "2px 0 8px rgba(0,0,0,0.1)",
          display: { xs: "flex", md: "none" },
          flexDirection: "column",
          padding: "16px",
          overflowY: "auto",
          transition: "left 0.3s ease-in-out"
        }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography sx={{ fontSize: { xs: "16px", md: "18px" }, fontWeight: 600 }}>Categories</Typography>
            <Button
              onClick={() => setSubcategorySidebarOpen(false)}
              sx={{
                minWidth: "auto",
                padding: "4px",
                color: "#6B7280",
                display: { xs: "block", md: "none" },
                "&:hover": {
                  background: "#F3F4F6"
                }
              }}
            >
              <Close sx={{ fontSize: { xs: "20px", md: "24px" } }} />
            </Button>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
            <Button
              onClick={() => {
                setFilters({ ...Filters, subCategoryName: "" });
                setSubcategorySidebarOpen(false);
              }}
              sx={{
                textTransform: "none",
                justifyContent: "flex-start",
                fontSize: { xs: "13px", md: "14px" },
                color: !Filters.subCategoryName ? (brand?.themeColor || "#ED1E79") : (brand?.theme?.textSecondary || "#6B7280"),
                fontWeight: !Filters.subCategoryName ? 600 : 400,
                px: { xs: "12px", md: "16px" },
                py: { xs: "8px", md: "10px" },
                borderRadius: "8px",
                "&:hover": {
                  background: brand?.theme?.hoverBackground || "#F3F4F6"
                }
              }}
            >
              All
            </Button>
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", gap: { xs: "8px", md: "16px" }, marginTop: { xs: "12px", md: "20px" }, flex: 1, minHeight: 0, flexDirection: { xs: "column", md: "row" } }}>
        {/* Category Sidebar - Always visible on desktop */}
          {productsType === 'vegitables' && (
          <Box sx={{
            width: { xs: 0, md: "200px" },
            flexShrink: 0,
            display: { xs: "none", md: "flex" },
            flexDirection: "column",
            background: "#fff",
            borderRight: "1px solid #E2E8F0",
            paddingRight: "16px",
            overflowY: "auto",
            maxHeight: "100%",
            padding: { xs: "8px", md: "12px" }
          }}>
            <Typography sx={{ fontSize: { xs: "12px", md: "14px" }, fontWeight: 600, mb: { xs: 1, md: 1.5 }, paddingTop: { xs: "8px", md: "8px" } }}>
              Categories
            </Typography>
            <Button
              onClick={() => {
                setFilters({ ...Filters, categoryId: "" });
              }}
              sx={{
                textTransform: "none",
                justifyContent: "flex-start",
                fontSize: { xs: "13px", md: "13px" },
                color: !Filters.categoryId ? (brand?.themeColor || "#ED1E79") : (brand?.theme?.textSecondary || "#6B7280"),
                fontWeight: !Filters.categoryId ? 600 : 400,
                px: { xs: "12px", md: "8px" },
                py: { xs: "8px", md: "6px" },
                borderRadius: "8px",
                mb: { xs: 0.5, md: 0.5 },
                "&:hover": {
                  background: brand?.theme?.hoverBackground || "#F3F4F6"
                }
              }}
            >
              All
            </Button>
            {categories.map(cat => (
              <Button
                key={cat}
                onClick={() => {
                  setFilters({ ...Filters, categoryId: cat });
                }}
                sx={{
                  textTransform: "none",
                  justifyContent: "flex-start",
                  fontSize: { xs: "13px", md: "13px" },
                  color: Filters.categoryId === cat ? (brand?.themeColor || "#ED1E79") : (brand?.theme?.textSecondary || "#6B7280"),
                  fontWeight: Filters.categoryId === cat ? 600 : 400,
                  px: { xs: "12px", md: "8px" },
                  py: { xs: "8px", md: "6px" },
                  borderRadius: "8px",
                  mb: { xs: 0.5, md: 0.5 },
                  "&:hover": {
                    background: brand?.theme?.hoverBackground || "#F3F4F6"
                  }
                }}
              >
                {cat}
              </Button>
            ))}
          </Box>
        )}
        
        {/* Center - Product List */}
        <Box sx={{ 
          flex: { xs: "1 1 100%", md: "1 1 100%" }, 
          display: "flex", 
          flexDirection: "column", 
          paddingBottom: { xs: "20px", md: "20px" },
          marginRight: { xs: 0, md: 0 },
          maxWidth: { xs: "100%", md: "100%" },
          width: { xs: "100%", md: "auto" }
        }}>
          <Grid 
            container 
            columns={24} 
            sx={{ 
              justifyContent:"flex-start",
              // overflowY:"scroll", 
              alignItems: "flex-start",
              alignContent: "flex-start",
            }}
          >
            {(filtered.length > 0 ? filtered : productsFromStore).map((product, ind) => {
              const getPricing = (product) => {
                const caps = getCapsForHub(product);
                const selectedCap = selectedCaps[product.id] || caps[0]?.item_capacity;
                const cap = caps.find(c => c.item_capacity === selectedCap) || caps[0];

                if (!cap) {
                  return {
                    sellingPrice: product.item_selling_price || product.item_price || product.price || 0,
                    offerPrice: null,
                    hasOffer: false,
                  };
                }

                const sellingPrice = Number(cap.item_selling_price || cap.item_price || 0);
                const offerPrice = Number(cap.item_offers_price || 0);
                const hasOffer = Number(cap.item_special_offer) > 0;

                return {
                  sellingPrice,
                  offerPrice: hasOffer ? offerPrice : null,
                  hasOffer,
                };
              };

              const caps = getCapsForHub(product);
              const selCap = selectedCaps[product.id] || (caps[0]?.item_capacity ?? null);
              const selectedCapacityObj = caps.find(c => c.item_capacity === selCap) || caps[0] || null;
              const isCapacityOutOfStock =
                selectedCapacityObj?.item_available === false;
              const { sellingPrice, offerPrice, hasOffer } = getPricing(product);
              const displayPrice = hasOffer ? offerPrice : sellingPrice;
              const qty = getCartQuantity(product.id, selCap);
              const hubId = Array.isArray(selectedHub) ? selectedHub[0] : selectedHub;
              // ✅ Correct shared stock logic
              const totalCartWeight = getTotalCartWeight(product.id);
              const totalAvailable = getTotalInventoryUnits(product);
              const capacityWeight = getCapacityWeight(
                selectedCapacityObj?.capacityValue,
                selectedCapacityObj?.unit
              );

              // Total stock in base unit (grams/ml/pieces)
              const totalStock = totalAvailable;

              // Weight already used by cart (all capacities)
              const usedStock = totalCartWeight;

              // Available remaining stock
              const remainingStock = totalStock - usedStock;
              const backendTotalStock = totalAvailable; // original stock from backend
              const isBackendOutOfStock = backendTotalStock <= 0;
              // How many of THIS capacity can still be added
              const maxAllowedQty = capacityWeight > 0
                ? Math.floor(remainingStock / capacityWeight)
                : 0;

              // Disable + only when user reached limit
              const isOutOfStockByWeight =
                capacityWeight <= 0 ||
                remainingStock < capacityWeight;
              console.log(totalAvailable, capacityWeight, totalCartWeight)

              return (
                <Grid item xs={12} sm={8} md={6} lg={3} key={product.id}>
                  <Box
                    sx={{
                      borderRadius: "4px",
                      overflow: "hidden",
                      bgcolor: "#fff",
                      boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                      display: "flex",
                      flexDirection: "column",
                      height: "100%",
                      margin: "8px",
                      position: "relative",
                      padding: { xs: "10px", md: "12px" }
                    }}
                  >

                    <Box sx={{ position: "relative" }}>
                        <Box
                          component="img"
                          src={(product.image_urls && product.image_urls[0]) || "/logo512.png"}
                          alt={product.item_name}
                          onError={(e) => { e.currentTarget.src = "/logo512.png" }}
                          sx={{
                            width: "100%",
                            height: { xs: 150, md: 200 },
                            objectFit: "cover",
                            background: "#F3F4F6",
                            display: "block",
                            borderRadius: "4px"
                          }}
                        />

                        {isBackendOutOfStock && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "20%", 
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: "linear-gradient(to top, rgba(0,0,0,0.65), rgba(60,60,60,0.45))",
                              // borderBottomLeftRadius: "4px",
                              // borderBottomRightRadius: "4px",
                            }}
                          >
                            <Typography
                              sx={{
                                color: "#fff",
                                fontSize: "13px",
                                fontWeight: 700,
                                letterSpacing: "1px",
                              }}
                            >
                              OUT OF STOCK
                            </Typography>
                          </Box>
                        )}
                    </Box>

                    <Box sx={{ paddingTop: { xs: "10px", md: "12px" },paddingBottom: { xs: "10px", md: "12px" }, display: "flex", flexDirection: "column", gap: 1 ,minHeight:"150px",justifyContent:"space-between"}}>
                      <Box
                          sx={{
                            paddingTop: { xs: "10px", md: "12px" },
                            paddingBottom: { xs: "10px", md: "12px" },
                            display: "flex",
                            flexDirection: "column",
                            gap: 1,
                            flexGrow: 1,
                            justifyContent: "space-between",
                          }}
                        >
                        <Box sx={{ position: "relative" }}>
                          <Typography
                            ref={(el) => {
                              if (!el) return;
                              const isOverflowing = el.scrollHeight > el.clientHeight;
                              setOverflowMap(prev => {
                                if (prev[product.id] === isOverflowing) return prev;
                                return { ...prev, [product.id]: isOverflowing };
                              });
                            }}
                            sx={{
                              fontSize: { xs: "12px", md: "13px" },
                              fontWeight: 600,
                              color: brand?.theme?.textPrimary || "#111827",
                              lineHeight: 1.3,
                              display: expandedTitles[product.id] ? "block" : "-webkit-box",
                              WebkitLineClamp: expandedTitles[product.id] ? "unset" : 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              textOverflow: expandedTitles[product.id] ? "unset" : "ellipsis",
                              pr: overflowMap[product.id] && !expandedTitles[product.id] ? "45px" : 0,
                              minHeight: expandedTitles[product.id] ? "auto" : "34px",
                            }}
                          >
                            {product.item_name}
                          </Typography>

                          {(overflowMap[product.id] || expandedTitles[product.id]) && (
                              expandedTitles[product.id] ? (
                                // EXPANDED → show inline "less"
                                <Typography
                                  onClick={() =>
                                    setExpandedTitles(prev => ({
                                      ...prev,
                                      [product.id]: false
                                    }))
                                  }
                                  sx={{
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: brand?.themeColor || "#1976d2",
                                    cursor: "pointer",
                                    mt: 0.5
                                  }}
                                >
                                  less
                                </Typography>
                              ) : (
                                // COLLAPSED → overlay "more"
                                <Box
                                  onClick={() =>
                                    setExpandedTitles(prev => ({
                                      ...prev,
                                      [product.id]: true
                                    }))
                                  }
                                  sx={{
                                    position: "absolute",
                                    bottom: 0,
                                    right: 0,
                                    top: "1.3em",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: brand?.themeColor || "#1976d2",
                                    cursor: "pointer",
                                    background: "#fff",
                                    pl: 0.5
                                  }}
                                >
                                  more
                                </Box>
                              )
                            )}
                        </Box>

                      {caps.length > 0 && (
                        <Box sx={{ 
                          width: "100%", 
                          overflowX: "auto", 
                          pb: 0.5,
                          scrollbarWidth: "none",
                          msOverflowStyle: "none",
                          "&::-webkit-scrollbar": { display: "none" }
                        }}>
                          <Stack direction="row" spacing={1} sx={{ minWidth: "max-content" }}>
                            {caps.map((cap) => {
                              const primaryColor = brand?.themeColor || "#1976d2";
                              const isSelected = selCap === cap.item_capacity;
                              return (
                                <Chip
                                  key={`${product.id}-${cap.item_capacity}`}
                                  label={`${cap.item_capacity}`}
                                  size="small"
                                  onClick={() => setSelectedCaps(prev => ({ ...prev, [product.id]: cap.item_capacity }))}
                                  sx={{
                                    borderRadius: "20px",
                                    border: isSelected ? "none" : `1px solid ${brand?.theme?.border || "#E5E7EB"}`,
                                    height: "29px",
                                    px: 1,
                                    background: isSelected ? primaryColor : "#fff",
                                    color: isSelected ? "white" : (brand?.theme?.textPrimary || "#374151"),
                                    fontSize: { xs: "10px", md: "12px" },
                                    fontWeight: isSelected ? 600 : 400,
                                    whiteSpace: "nowrap",
                                    "&:hover": {
                                      background: isSelected ? primaryColor : (brand?.theme?.hoverBackground || "#F3F4F6")
                                    }
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        </Box>
                      )}
                      </Box>


                     

                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>

                        {/* PRICE */}
                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
                          {hasOffer && (
                            <Typography
                              sx={{
                                fontSize: "12px",
                                color: brand?.theme?.textSecondary || "#6B7280",
                                textDecoration: "line-through",
                                lineHeight: 1,
                              }}
                            >
                              ₹{sellingPrice}
                            </Typography>
                          )}

                          <Typography
                            sx={{
                              fontSize: "16px",
                              fontWeight: 700,
                              color: brand?.theme?.textPrimary || "#111827",
                              lineHeight: 1.2,
                            }}
                          >
                            ₹{displayPrice}
                          </Typography>
                        </Box>
                      <AddToCartButton
                        productId={product.id}
                        inventoryId={hubId}
                        inventoryDetailsId={product.inventryDetails?.id || product.inventory_details_id}
                        selectedCapacity={selectedCapacityObj}
                        totalAmount={displayPrice}
                        quantities={qty}
                        disabled={isCapacityOutOfStock}
                        disableIncrement={isOutOfStockByWeight || isCapacityOutOfStock}
                        onUpdateCart={handleUpdateCart}
                        onStockLimit={() => {
                          setStockAlertProduct(product.id);
                          setTimeout(() => {
                            setStockAlertProduct(null);
                          }, 2000);
                        }}
                      />
                      </Box>

                    </Box>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Box>
      {!hideFooter && footerText && (
        <Box
          sx={{
            width: "100%",
            padding: "12px 24px",
            backgroundColor: brand?.backgroundColor || "#F9FAFB",
            borderTop: "1px solid #E5E7EB",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "auto",
          }}
        >
          <Typography
              sx={{
                fontSize: "12px",
                fontWeight: "400",
                color: brand?.theme?.textSecondary || "#6B7280",
                textAlign: "center",
              }}
          >
            {"\u00A9 "}{footerText}
          </Typography>
        </Box>
      )}
      <Snackbar
        open={Boolean(stockAlertProduct)}
        autoHideDuration={2000}
        onClose={() => setStockAlertProduct(null)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          top: "50px", // adjust if needed
        }}
      >
        <Box
          sx={{
            backgroundColor: "#111",
            color: "#fff",
            px: 3,
            py: 1.5,
            borderRadius: "10px",
            fontSize: "14px",
            fontWeight: 500,
            boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
          }}
        >
          That's all we have in stock
        </Box>
      </Snackbar>
    </Box>
  );
};

export default Home;
